/**
 * RetrievalPoliciesAdmin Component
 *
 * Purpose: Admin interface for managing retrieval_policies
 *          (per-airline/fleet/pipeline-stage retrieval configuration).
 *
 * Features:
 * - View all retrieval policies with airline, fleet, stage, and competency
 * - Create new policies for specific airline + fleet + pipeline stage
 * - Edit core parameters (k_recall, k_final, alpha_hybrid, mmr_lambda, thresholds, use_reranker)
 * - Delete policies (falls back to global ORCA360 defaults)
 *
 * Permissions: Super Admin only via /admin/retrieval-policies route wrapper.
 */

import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Airline, FleetModel } from '../../lib/types';
import {
  Settings,
  Loader,
  Plus,
  Edit,
  Trash2,
  Save,
  Play,
} from 'lucide-react';

type PipelineStage =
  | 'classify'
  | 'assess_pilot_pass1'
  | 'assess_pilot_pass2'
  | 'assess_iec_pass1'
  | 'assess_iec_pass2';

interface RetrievalPolicy {
  id: string;
  airline_id: string;
  fleet_model_id?: string | null;
  pipeline_stage: PipelineStage;
  competency_code?: string | null;
  k_recall?: number | null;
  k_rerank?: number | null;
  k_final?: number | null;
  alpha_hybrid?: number | null;
  mmr_lambda?: number | null;
  min_similarity?: number | null;
  min_reranker_score?: number | null;
  use_reranker?: boolean | null;
  max_context_tokens?: number | null;
  created_at?: string;
  updated_at?: string;
}

interface PolicyWithNames extends RetrievalPolicy {
  airline_name?: string;
  airline_code?: string;
  fleet_code?: string;
  fleet_name?: string;
}

const PIPELINE_STAGE_LABELS: Record<PipelineStage, string> = {
  classify: 'Classify (Event/Competency)',
  assess_pilot_pass1: 'Assess Pilot – Pass 1 (Evidence)',
  assess_pilot_pass2: 'Assess Pilot – Pass 2 (Grades)',
  assess_iec_pass1: 'Assess IEC – Pass 1 (Evidence)',
  assess_iec_pass2: 'Assess IEC – Pass 2 (Grades)',
};

export const RetrievalPoliciesAdmin: React.FC = () => {
  const [policies, setPolicies] = useState<PolicyWithNames[]>([]);
  const [airlines, setAirlines] = useState<Airline[]>([]);
  const [fleets, setFleets] = useState<FleetModel[]>([]);
  const [loading, setLoading] = useState(true);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyWithNames | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testQuery, setTestQuery] = useState('');
  const [testResults, setTestResults] = useState<
    Array<{ id: string; score: number; title?: string; contentPreview: string }>
  >([]);
  const [testError, setTestError] = useState<string | null>(null);

  const [newPolicy, setNewPolicy] = useState<{
    airline_id: string;
    fleet_model_id: string;
    pipeline_stage: PipelineStage;
    competency_code: string;
  }>({
    airline_id: '',
    fleet_model_id: '',
    pipeline_stage: 'assess_pilot_pass2',
    competency_code: '',
  });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [{ data: airlineData }, { data: fleetData }] = await Promise.all([
        supabase.from('airlines').select('*').order('code'),
        supabase.from('fleet_models').select('*').order('code'),
      ]);

      setAirlines(airlineData || []);
      setFleets(fleetData || []);

      const { data: policyData, error } = await supabase
        .from('retrieval_policies')
        .select('*')
        .order('airline_id')
        .order('pipeline_stage')
        .order('competency_code', { ascending: true });

      if (error) {
        console.error('[RetrievalPoliciesAdmin] Load policies error:', error);
        alert('Failed to load retrieval policies.');
        return;
      }

      const enriched: PolicyWithNames[] = (policyData || []).map((p: any) => {
        const airline = (airlineData || []).find((a) => a.id === p.airline_id);
        const fleet = (fleetData || []).find((f) => f.id === p.fleet_model_id);
        return {
          ...p,
          airline_name: airline?.name,
          airline_code: airline?.code,
          fleet_code: fleet?.code,
          fleet_name: fleet?.name,
        };
      });

      setPolicies(enriched);
    } catch (err) {
      console.error('[RetrievalPoliciesAdmin] Unexpected error:', err);
      alert('An unexpected error occurred while loading retrieval policies.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePolicy = async () => {
    if (!newPolicy.airline_id || !newPolicy.pipeline_stage) {
      alert('Please select an airline and pipeline stage.');
      return;
    }

    setIsSaving(true);
    try {
      const { error } = await supabase.from('retrieval_policies').insert({
        airline_id: newPolicy.airline_id,
        fleet_model_id: newPolicy.fleet_model_id || null,
        pipeline_stage: newPolicy.pipeline_stage,
        competency_code: newPolicy.competency_code || null,
      });

      if (error) {
        console.error('[RetrievalPoliciesAdmin] Create error:', error);
        alert('Failed to create retrieval policy.');
        return;
      }

      alert('Retrieval policy created successfully.');
      setShowCreateModal(false);
      setNewPolicy({
        airline_id: '',
        fleet_model_id: '',
        pipeline_stage: 'assess_pilot_pass2',
        competency_code: '',
      });
      await loadAll();
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdatePolicy = async () => {
    if (!selectedPolicy) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('retrieval_policies')
        .update({
          k_recall: selectedPolicy.k_recall,
          k_rerank: selectedPolicy.k_rerank,
          k_final: selectedPolicy.k_final,
          alpha_hybrid: selectedPolicy.alpha_hybrid,
          mmr_lambda: selectedPolicy.mmr_lambda,
          min_similarity: selectedPolicy.min_similarity,
          min_reranker_score: selectedPolicy.min_reranker_score,
          use_reranker: selectedPolicy.use_reranker,
          max_context_tokens: selectedPolicy.max_context_tokens,
        })
        .eq('id', selectedPolicy.id);

      if (error) {
        console.error('[RetrievalPoliciesAdmin] Update error:', error);
        alert('Failed to update retrieval policy.');
        return;
      }

      alert('Retrieval policy updated successfully.');
      setShowEditModal(false);
      setSelectedPolicy(null);
      await loadAll();
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestRetrieval = async () => {
    if (!selectedPolicy) {
      alert('Select a policy to test.');
      return;
    }
    if (!testQuery.trim()) {
      alert('Enter a query text to test retrieval.');
      return;
    }

    setIsTesting(true);
    setTestError(null);
    setTestResults([]);

    try {
      // Get the current session token for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session. Please log in again.');
      }

      const payload = {
        airlineId: selectedPolicy.airline_id,
        fleetModelId: selectedPolicy.fleet_model_id || null,
        pipelineStage: selectedPolicy.pipeline_stage,
        competencyCode: selectedPolicy.competency_code || null,
        query: testQuery.trim(),
      };

      const response = await fetch('/api/test-retrieval', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || `HTTP ${response.status}`);
      }

      const data = await response.json();
      const chunks = (data && data.chunks) || [];

      const mapped = chunks.map((c: any) => ({
        id: c.id ?? c.chunkId ?? 'unknown',
        score: c.finalScore ?? c.vectorScore ?? 0,
        title: c.docTitle ?? c.title ?? undefined,
        contentPreview: (c.content || '').slice(0, 240),
      }));

      setTestResults(mapped);
    } catch (err: any) {
      console.error('[RetrievalPoliciesAdmin] Test retrieval error:', err);
      setTestError(err?.message || 'Unexpected error during test retrieval.');
    } finally {
      setIsTesting(false);
    }
  };

  const handleDeletePolicy = async (id: string) => {
    if (!confirm('Delete this retrieval policy? Pipelines will fall back to ORCA360 defaults.')) {
      return;
    }

    const { error } = await supabase.from('retrieval_policies').delete().eq('id', id);
    if (error) {
      console.error('[RetrievalPoliciesAdmin] Delete error:', error);
      alert('Failed to delete retrieval policy.');
      return;
    }

    alert('Retrieval policy deleted.');
    await loadAll();
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-navy">Retrieval Policies</h1>
          <p className="text-steel mt-1 text-sm">
            Configure per-airline/fleet retrieval parameters for each pipeline stage. These settings control
            how many chunks are recalled, reranked, and injected into LLM prompts.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition text-sm"
        >
          <Plus size={18} />
          New Policy
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-xs text-blue-900">
        <p className="font-semibold mb-1">How Retrieval Policies Work</p>
        <p>
          Retrieval policies layer on top of <strong>Doc Types</strong> and <strong>Airline Doc Type Overrides</strong>.
          Use them to tune <strong>k_recall / k_final / α / λ / thresholds</strong> per pipeline stage and competency,
          while doc types continue to control type-level boosts and defaults.
        </p>
      </div>

      {/* Policies Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-orca-elevate" size={32} />
        </div>
      ) : policies.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <Settings className="mx-auto text-steel/50" size={48} />
          <p className="text-steel mt-4 text-sm">
            No retrieval policies configured yet. Pipelines are using ORCA360 default behavior.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-navy text-white">
              <tr>
                <th className="px-3 py-2 text-left">Airline</th>
                <th className="px-3 py-2 text-left">Fleet</th>
                <th className="px-3 py-2 text-left">Stage</th>
                <th className="px-3 py-2 text-left">Competency</th>
                <th className="px-3 py-2 text-center">k_recall</th>
                <th className="px-3 py-2 text-center">k_final</th>
                <th className="px-3 py-2 text-center">α</th>
                <th className="px-3 py-2 text-center">λ (MMR)</th>
                <th className="px-3 py-2 text-center">use_reranker</th>
                <th className="px-3 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {policies.map((p) => (
                <tr 
                  key={p.id} 
                  className={`border-b border-steel/20 hover:bg-ice/30 transition ${
                    selectedPolicy?.id === p.id ? 'bg-sky-50 border-l-4 border-l-sky-500' : ''
                  }`}
                >
                  <td className="px-3 py-2">
                    <div>
                      <p className="font-semibold text-navy">{p.airline_name || 'Unknown Airline'}</p>
                      <p className="text-steel text-[10px]">{p.airline_code}</p>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {p.fleet_code ? (
                      <span className="inline-flex items-center px-2 py-0.5 bg-orca-elevate/10 text-orca-elevate rounded-full text-[10px]">
                        {p.fleet_code}
                      </span>
                    ) : (
                      <span className="text-steel/60 text-[10px]">All Fleets</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className="text-[11px] font-medium text-navy">
                      {PIPELINE_STAGE_LABELS[p.pipeline_stage] || p.pipeline_stage}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    {p.competency_code || <span className="text-steel/60 italic">All</span>}
                  </td>
                  <td className="px-3 py-2 text-center">{p.k_recall ?? '-'}</td>
                  <td className="px-3 py-2 text-center">{p.k_final ?? '-'}</td>
                  <td className="px-3 py-2 text-center">{p.alpha_hybrid ?? '-'}</td>
                  <td className="px-3 py-2 text-center">{p.mmr_lambda ?? '-'}</td>
                  <td className="px-3 py-2 text-center">
                    {p.use_reranker === false ? (
                      <span className="text-red-500 font-semibold text-[11px]">OFF</span>
                    ) : (
                      <span className="text-green-600 font-semibold text-[11px]">ON</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => {
                          setSelectedPolicy(p);
                          setTestQuery('');
                          setTestResults([]);
                          setTestError(null);
                        }}
                        className={`p-1 rounded transition ${
                          selectedPolicy?.id === p.id
                            ? 'bg-sky-100 text-sky-700'
                            : 'hover:bg-sky-50 text-sky-600'
                        }`}
                        title="Select for Testing"
                      >
                        <Play size={14} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedPolicy(p);
                          setShowEditModal(true);
                        }}
                        className="p-1 hover:bg-orca-elevate/10 rounded transition"
                        title="Edit"
                      >
                        <Edit size={14} className="text-orca-elevate" />
                      </button>
                      <button
                        onClick={() => handleDeletePolicy(p.id)}
                        className="p-1 hover:bg-red-100 rounded"
                        title="Delete"
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Test Retrieval Panel */}
      <div className={`mt-6 rounded-lg shadow-md p-4 text-xs transition-all ${
        selectedPolicy ? 'bg-sky-50 border-2 border-sky-300' : 'bg-white border-2 border-gray-200'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="font-semibold text-navy flex items-center gap-2">
              <Play size={16} className={selectedPolicy ? 'text-sky-600' : 'text-gray-400'} />
              Test Retrieval for Selected Policy
            </p>
            <p className="text-steel text-[11px] mt-1">
              {selectedPolicy ? (
                <>Click the <Play size={12} className="inline" /> icon in the Actions column to select a policy for testing.</>
              ) : (
                'Select a policy by clicking the play icon in the Actions column above.'
              )}
            </p>
          </div>
          <button
            onClick={handleTestRetrieval}
            disabled={isTesting || !selectedPolicy}
            className="inline-flex items-center gap-1 px-4 py-2 rounded bg-orca-elevate text-white hover:bg-orca-elevate/90 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium"
          >
            {isTesting ? (
              <>
                <Loader size={16} className="animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Play size={16} />
                Run Test
              </>
            )}
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <label className="block text-steel mb-1 font-medium">Test Query</label>
            <textarea
              rows={4}
              value={testQuery}
              onChange={(e) => setTestQuery(e.target.value)}
              disabled={!selectedPolicy}
              placeholder={selectedPolicy 
                ? "Describe a scenario, competency, or IEC situation to test retrieval (e.g., COM callouts during TCAS RA on A320)."
                : "Select a policy first by clicking the play icon in the table above"
              }
              className={`w-full px-3 py-2 border rounded resize-none ${
                selectedPolicy 
                  ? 'border-sky-300 bg-white focus:ring-2 focus:ring-sky-500 focus:border-sky-500' 
                  : 'border-gray-300 bg-gray-50 cursor-not-allowed'
              }`}
            />
            {selectedPolicy && (
              <div className="mt-2 p-2 bg-sky-100 border border-sky-200 rounded">
                <p className="text-[11px] text-sky-900">
                  <span className="font-semibold">Selected Policy:</span>{' '}
                  {selectedPolicy.airline_code} · {PIPELINE_STAGE_LABELS[selectedPolicy.pipeline_stage]}
                  {selectedPolicy.competency_code && ` · ${selectedPolicy.competency_code}`}
                  {selectedPolicy.fleet_code && ` · Fleet: ${selectedPolicy.fleet_code}`}
                </p>
              </div>
            )}
          </div>

          <div className="flex-1">
            <label className="block text-steel mb-1">Results</label>
            <div className="border border-steel/30 rounded p-2 h-32 overflow-y-auto bg-ice/10">
              {testError && (
                <p className="text-red-600 text-[11px] mb-1">Error: {testError}</p>
              )}
              {!testError && testResults.length === 0 && (
                <p className="text-steel/70 text-[11px]">
                  No results yet. Enter a query and click "Run Test" after selecting a policy.
                </p>
              )}
              {testResults.length > 0 && (
                <ul className="space-y-1">
                  {testResults.map((r) => (
                    <li key={r.id} className="border-b border-steel/10 pb-1 mb-1 last:border-b-0 last:pb-0 last:mb-0">
                      <p className="text-[11px] font-semibold text-navy flex justify-between">
                        <span>{r.title || 'Untitled Document'}</span>
                        <span className="font-mono text-[10px] text-steel">score {r.score.toFixed(3)}</span>
                      </p>
                      <p className="text-[11px] text-steel line-clamp-2">
                        {r.contentPreview}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-navy mb-4">Create Retrieval Policy</h2>

            <div className="space-y-3 text-sm">
              <div>
                <label className="block text-steel mb-1">Airline</label>
                <select
                  value={newPolicy.airline_id}
                  onChange={(e) => setNewPolicy({ ...newPolicy, airline_id: e.target.value })}
                  className="w-full px-3 py-2 border border-steel/30 rounded"
                >
                  <option value="">Select Airline</option>
                  {airlines.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-steel mb-1">Fleet (optional)</label>
                <select
                  value={newPolicy.fleet_model_id}
                  onChange={(e) => setNewPolicy({ ...newPolicy, fleet_model_id: e.target.value })}
                  className="w-full px-3 py-2 border border-steel/30 rounded"
                >
                  <option value="">All Fleets</option>
                  {fleets.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({f.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-steel mb-1">Pipeline Stage</label>
                <select
                  value={newPolicy.pipeline_stage}
                  onChange={(e) =>
                    setNewPolicy({ ...newPolicy, pipeline_stage: e.target.value as PipelineStage })
                  }
                  className="w-full px-3 py-2 border border-steel/30 rounded"
                >
                  {Object.entries(PIPELINE_STAGE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-steel mb-1">Competency Code (optional)</label>
                <input
                  type="text"
                  value={newPolicy.competency_code}
                  onChange={(e) => setNewPolicy({ ...newPolicy, competency_code: e.target.value })}
                  placeholder="e.g., COM, SAW, IEC1"
                  className="w-full px-3 py-2 border border-steel/30 rounded"
                />
              </div>

              <p className="text-xs text-steel bg-white p-2 rounded border border-steel/20">
                After creating the policy, you can fine-tune parameters such as k_recall, k_final, α, λ, and
                thresholds to control retrieval behavior for this stage.
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewPolicy({
                    airline_id: '',
                    fleet_model_id: '',
                    pipeline_stage: 'assess_pilot_pass2',
                    competency_code: '',
                  });
                }}
                className="px-3 py-1.5 border border-steel/30 rounded hover:bg-steel/10 transition text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePolicy}
                className="px-3 py-1.5 bg-orca-elevate text-white rounded hover:bg-orca-elevate/90 transition text-xs flex items-center gap-1"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader size={14} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Plus size={14} />
                    Create Policy
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-steel/20">
              <div>
                <h2 className="text-lg font-bold text-navy">
                  {selectedPolicy.airline_name} – {PIPELINE_STAGE_LABELS[selectedPolicy.pipeline_stage]}
                </h2>
                <p className="text-[11px] text-steel">
                  Fleet:{' '}
                  {selectedPolicy.fleet_code
                    ? `${selectedPolicy.fleet_name} (${selectedPolicy.fleet_code})`
                    : 'All Fleets'}{' '}
                  · Competency: {selectedPolicy.competency_code || 'All'}
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="block text-steel mb-0.5">k_recall</label>
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={selectedPolicy.k_recall ?? ''}
                    onChange={(e) =>
                      setSelectedPolicy({
                        ...selectedPolicy,
                        k_recall: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      })
                    }
                    className="w-full px-2 py-1 border border-steel/30 rounded"
                  />
                </div>

                <div>
                  <label className="block text-steel mb-0.5">k_final</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={selectedPolicy.k_final ?? ''}
                    onChange={(e) =>
                      setSelectedPolicy({
                        ...selectedPolicy,
                        k_final: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      })
                    }
                    className="w-full px-2 py-1 border border-steel/30 rounded"
                  />
                </div>

                <div>
                  <label className="block text-steel mb-0.5">alpha_hybrid (α)</label>
                  <input
                    type="number"
                    step={0.05}
                    min={0}
                    max={1}
                    value={selectedPolicy.alpha_hybrid ?? ''}
                    onChange={(e) =>
                      setSelectedPolicy({
                        ...selectedPolicy,
                        alpha_hybrid: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    className="w-full px-2 py-1 border border-steel/30 rounded"
                  />
                </div>

                <div>
                  <label className="block text-steel mb-0.5">mmr_lambda (λ)</label>
                  <input
                    type="number"
                    step={0.05}
                    min={0}
                    max={1}
                    value={selectedPolicy.mmr_lambda ?? ''}
                    onChange={(e) =>
                      setSelectedPolicy({
                        ...selectedPolicy,
                        mmr_lambda: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    className="w-full px-2 py-1 border border-steel/30 rounded"
                  />
                </div>

                <div>
                  <label className="block text-steel mb-0.5">min_similarity</label>
                  <input
                    type="number"
                    step={0.05}
                    min={0}
                    max={1}
                    value={selectedPolicy.min_similarity ?? ''}
                    onChange={(e) =>
                      setSelectedPolicy({
                        ...selectedPolicy,
                        min_similarity: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    className="w-full px-2 py-1 border border-steel/30 rounded"
                  />
                </div>

                <div>
                  <label className="block text-steel mb-0.5">min_reranker_score</label>
                  <input
                    type="number"
                    step={0.05}
                    min={0}
                    max={1}
                    value={selectedPolicy.min_reranker_score ?? ''}
                    onChange={(e) =>
                      setSelectedPolicy({
                        ...selectedPolicy,
                        min_reranker_score: e.target.value ? parseFloat(e.target.value) : undefined,
                      })
                    }
                    className="w-full px-2 py-1 border border-steel/30 rounded"
                  />
                </div>

                <div>
                  <label className="block text-steel mb-0.5">max_context_tokens</label>
                  <input
                    type="number"
                    min={200}
                    max={4000}
                    step={100}
                    value={selectedPolicy.max_context_tokens ?? ''}
                    onChange={(e) =>
                      setSelectedPolicy({
                        ...selectedPolicy,
                        max_context_tokens: e.target.value ? parseInt(e.target.value, 10) : undefined,
                      })
                    }
                    className="w-full px-2 py-1 border border-steel/30 rounded"
                  />
                </div>

                <div className="flex items-center gap-2 mt-4">
                  <input
                    id="use_reranker"
                    type="checkbox"
                    checked={selectedPolicy.use_reranker !== false}
                    onChange={(e) =>
                      setSelectedPolicy({
                        ...selectedPolicy,
                        use_reranker: e.target.checked,
                      })
                    }
                    className="w-4 h-4"
                  />
                  <label htmlFor="use_reranker" className="text-steel text-xs">
                    Use LLM reranker for this stage
                  </label>
                </div>
              </div>

              <p className="text-[10px] text-steel mt-4">
                Leave fields empty to fall back to ORCA360 defaults (k_recall=24, k_final=6, α=0.7, λ=0.5,
                min_similarity=0.3).
              </p>
            </div>

            {/* Footer */}
            <div className="border-t border-steel/20 px-6 py-3 bg-white flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedPolicy(null);
                }}
                className="px-3 py-1.5 border border-steel/30 rounded hover:bg-steel/10 transition text-xs"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePolicy}
                className="px-3 py-1.5 bg-orca-elevate text-white rounded hover:bg-orca-elevate/90 transition text-xs flex items-center gap-1"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader size={14} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={14} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RetrievalPoliciesAdmin;


